import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Calendar, MapPin, Sparkles } from "lucide-react";
import { Container, FadeIn } from "@/components/Section";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * FeaturedEvents — premium homepage strip.
 *
 * Pulls events flagged ``featured=true`` from the CMS.  Designed to feel
 * like a flagship moment, not a generic listing: large hero card on the
 * left, supporting cards stacked on the right, subtle hover motion,
 * always drives "Book" or "See what's on" CTAs.
 *
 * Renders nothing when there are no featured events — homepage spacing
 * stays clean.
 */
export default function FeaturedEvents({ testId = "home-featured-events" }) {
  const [events, setEvents] = useState([]);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/events?featured=true`)
      .then((r) => {
        if (cancelled) return;
        setEvents(r.data || []);
      })
      .catch(() => {})
      .finally(() => !cancelled && setReady(true));
    return () => {
      cancelled = true;
    };
  }, []);

  if (!ready || events.length === 0) return null;

  const [hero, ...rest] = events;
  const supporting = rest.slice(0, 3);

  return (
    <section
      data-testid={testId}
      className="relative bg-black border-y border-amber-500/10 overflow-hidden"
    >
      {/* Atmospheric amber halos */}
      <div className="pointer-events-none absolute -left-32 top-1/3 h-[26rem] w-[26rem] rounded-full bg-amber-400/[0.05] blur-[120px]" />
      <div className="pointer-events-none absolute right-0 -bottom-24 h-[24rem] w-[24rem] rounded-full bg-amber-400/[0.04] blur-[140px]" />

      <Container className="relative py-24 sm:py-28 lg:py-32">
        <FadeIn className="max-w-2xl">
          <p className="text-[10px] uppercase tracking-[0.45em] text-amber-400/80 font-bold inline-flex items-center gap-3">
            <Sparkles className="h-3 w-3" /> Featured this season
          </p>
          <h2 className="mt-5 font-display font-black uppercase tracking-tighter leading-[0.95] text-white text-3xl sm:text-4xl lg:text-5xl">
            Step into what&rsquo;s next
          </h2>
          <p className="mt-5 text-zinc-400 leading-relaxed text-base sm:text-lg max-w-xl">
            Curated by the temple — flagship experiences, retreats and rituals
            we&rsquo;re putting our soul into.
          </p>
        </FadeIn>

        <div className="mt-14 grid lg:grid-cols-12 gap-5 lg:gap-7">
          <FadeIn delay={0.05} className="lg:col-span-7">
            <HeroEventCard event={hero} />
          </FadeIn>
          <div className="lg:col-span-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-5 lg:gap-5">
            {supporting.map((e, i) => (
              <FadeIn key={e.id} delay={0.12 + i * 0.05}>
                <SupportingEventCard event={e} />
              </FadeIn>
            ))}
          </div>
        </div>

        <FadeIn delay={0.2}>
          <div className="mt-14 sm:mt-16 border-t border-amber-500/10 pt-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
            <div>
              <p className="text-[10px] uppercase tracking-[0.4em] text-amber-400/70 font-bold">
                Every season is a chapter
              </p>
              <p className="mt-2 font-display font-black text-xl sm:text-2xl">
                See the whole programme
              </p>
            </div>
            <Link
              to="/whats-on"
              data-testid="featured-events-see-all"
              className="group inline-flex items-center gap-3 bg-amber-400 hover:bg-amber-300 text-black px-6 py-4 text-[11px] uppercase tracking-[0.25em] font-bold transition-colors"
            >
              See what&rsquo;s on
              <ArrowRight className="h-3.5 w-3.5 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </FadeIn>
      </Container>
    </section>
  );
}

function HeroEventCard({ event }) {
  return (
    <motion.div
      whileHover={{ y: -4 }}
      transition={{ duration: 0.4, ease: "easeOut" }}
      className="h-full"
    >
      <Link
        to={`/events/${event.id}`}
        data-testid={`featured-event-hero-${event.id}`}
        className="group relative block overflow-hidden bg-zinc-950 border border-white/[0.06] hover:border-amber-400/60 transition-all duration-500 h-full aspect-[4/5] lg:aspect-[5/4]"
      >
        <motion.div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${event.image})` }}
          initial={{ scale: 1.02 }}
          whileHover={{ scale: 1.08 }}
          transition={{ duration: 2, ease: "easeOut" }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/55 to-transparent" />
        <div className="absolute inset-x-0 top-0 h-1/3 bg-gradient-to-b from-black/40 to-transparent" />

        {event.tag && (
          <span className="absolute top-5 left-5 inline-flex items-center px-3 py-1.5 bg-amber-400 text-black text-[10px] uppercase tracking-[0.3em] font-bold">
            {event.tag}
          </span>
        )}

        <div className="absolute inset-x-0 bottom-0 p-7 sm:p-9">
          <p className="text-[10px] uppercase tracking-[0.4em] text-amber-400 font-bold inline-flex items-center gap-2">
            <Calendar className="h-3 w-3" /> {event.date} · {event.time}
          </p>
          <p className="mt-4 font-display font-black uppercase leading-[1.0] tracking-tight text-white text-3xl sm:text-4xl lg:text-5xl">
            {event.title}
          </p>
          <p className="mt-4 text-zinc-300/90 leading-relaxed text-sm sm:text-base max-w-md">
            {event.description?.slice(0, 140)}
            {event.description?.length > 140 ? "…" : ""}
          </p>
          <div className="mt-6 flex items-center gap-5 flex-wrap">
            <span className="inline-flex items-center gap-2 text-xs text-zinc-400">
              <MapPin className="h-3 w-3" /> {event.location}
            </span>
            <span className="text-xs text-amber-400/90 font-bold">
              {event.price}
            </span>
            <span className="inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.25em] text-amber-400 font-bold group-hover:translate-x-1 transition-transform">
              Book <ArrowRight className="h-3 w-3" />
            </span>
          </div>
        </div>
      </Link>
    </motion.div>
  );
}

function SupportingEventCard({ event }) {
  return (
    <motion.div
      whileHover={{ y: -2 }}
      transition={{ duration: 0.4, ease: "easeOut" }}
    >
      <Link
        to={`/events/${event.id}`}
        data-testid={`featured-event-${event.id}`}
        className="group relative flex flex-col overflow-hidden bg-zinc-950 border border-white/[0.06] hover:border-amber-400/50 transition-all duration-500 aspect-[16/10] sm:aspect-[5/4] lg:aspect-[16/8]"
      >
        <motion.div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${event.image})` }}
          initial={{ scale: 1.01 }}
          whileHover={{ scale: 1.05 }}
          transition={{ duration: 1.5, ease: "easeOut" }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/45 to-transparent" />
        <div className="absolute inset-x-0 bottom-0 p-5">
          <p className="text-[10px] uppercase tracking-[0.35em] text-amber-400 font-bold">
            {event.date}
          </p>
          <p className="mt-2 font-display font-black uppercase text-base sm:text-lg leading-tight text-white">
            {event.title}
          </p>
        </div>
      </Link>
    </motion.div>
  );
}
